import { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, matchPath } from 'react-router-dom';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import { useColorScheme, alpha } from '@mui/material/styles';
import useConfig from 'hooks/useConfig';

// project imports
import { useMenuItems } from 'menu-items';
import useMenuCollapse from 'hooks/useMenuCollapse';
import { useGetMenuMaster } from 'api/menu';

// assets
import DownOutlined from '@ant-design/icons/DownOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';
import UpOutlined from '@ant-design/icons/UpOutlined';

// ==============================|| NAVIGATION ||============================== //

export default function Navigation() {
  const { pathname } = useLocation();
  const menuItems = useMenuItems();
  const { colorScheme } = useColorScheme();
  const mode = colorScheme;
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  
  const [selectedID, setSelectedID] = useState('');
  const [openItems, setOpenItems] = useState({});

  const handleToggle = (itemId) => {
    setOpenItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Exact route match only (do not treat parent as selected when a child is active)
  useEffect(() => {
    const findSelectedID = (items) => {
      for (const item of items) {
        if (item.url === pathname) {
          return item.id;
        }
        if (item.children && item.children.length > 0) {
          const childMatch = findSelectedID(item.children);
          if (childMatch) {
            return childMatch;
          }
        }
      }
      return null;
    };

    const newSelectedID = findSelectedID(menuItems.items);
    setSelectedID(newSelectedID || '');
  }, [pathname, menuItems.items]);

  // Text colors matching Mantis approach
  const textColor = mode === 'dark' ? 'grey.400' : 'text.primary';
  const iconSelectedColor = mode === 'dark' ? 'text.primary' : 'primary.main';

  // Parent row keeps full hit height; wash/hover is a shorter inset pill (even L/R)
  const parentRowHighlight = (theme) => ({
    position: 'relative',
    alignItems: 'center',
    bgcolor: 'transparent !important',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      height: 'calc(100% - 0.75rem)', // ~12px total vertical inset at 16px root
      borderRadius: 1,
      zIndex: 0,
      pointerEvents: 'none',
      bgcolor: 'transparent',
      boxSizing: 'border-box'
    },
    '& > *': { position: 'relative', zIndex: 1 },
    '&:hover': {
      bgcolor: 'transparent !important',
      '&::before': {
        bgcolor: theme.palette.primary.lighter,
        ...theme.applyStyles('dark', { bgcolor: theme.palette.divider })
      }
    },
    '&.Mui-selected': {
      bgcolor: 'transparent !important',
      color: iconSelectedColor,
      '&::before': {
        bgcolor: theme.palette.primary.lighter,
        ...theme.applyStyles('dark', { bgcolor: theme.palette.divider })
      },
      '&:hover': {
        bgcolor: 'transparent !important',
        color: iconSelectedColor,
        '&::before': {
          bgcolor: theme.palette.primary.lighter,
          ...theme.applyStyles('dark', { bgcolor: theme.palette.divider })
        }
      }
    }
  });

  const navItems = useMemo(() => {
    return menuItems.items.map((item) => {
      const level = 1; // All top-level items are level 1
      
      // Handle group type items (with children)
      if (item.type === 'group' || item.type === 'collapse') {
        const hasChildren = item.children && item.children.length > 0;
        const hasSelectedChild =
          hasChildren && item.children.some((c) => c.url === pathname || c.id === selectedID);
        // Full selected treatment only when this parent row is the active route
        const isSelected = Boolean(item.url) && item.url === pathname;
        const isExpanded = isSelected || hasSelectedChild || Boolean(openItems[item.id]);
        const hasUrl = item.url && item.url !== '';
        
        return (
          <Box key={item.id} sx={{ position: 'relative' }}>
            <ListItemButton
              component={hasUrl ? Link : 'div'}
              to={hasUrl ? item.url : undefined}
              selected={isSelected}
              sx={(theme) => ({
                zIndex: 1201,
                // Match L/R padding (MUI gutters default pr=16; pl was 2.5=20)
                ...(drawerOpen ? { pl: 2, pr: 2, minHeight: '3.25rem', py: 0, ...parentRowHighlight(theme) } : { pl: 1.5, pr: 1.5, py: 1.25 }),
                ...(!drawerOpen && {
                  '&:hover': { bgcolor: 'transparent' },
                  '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' }
                })
              })}
              onClick={(e) => {
                // Only prevent navigation if clicking the arrow, not the main item
                if (e.target.closest('[data-toggle]')) {
                  e.preventDefault();
                  e.stopPropagation();
                  handleToggle(item.id);
                }
              }}
            >
              {item.icon && (
                <ListItemIcon
                  sx={(theme) => ({
                    minWidth: 28,
                    color: isSelected ? iconSelectedColor : textColor,
                    ...(!drawerOpen && {
                      borderRadius: 1.5,
                      width: 36,
                      height: 36,
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': { bgcolor: 'secondary.lighter', ...theme.applyStyles('dark', { bgcolor: 'secondary.light' }) }
                    }),
                    ...(!drawerOpen &&
                      isSelected && {
                        bgcolor: 'primary.lighter',
                        ...theme.applyStyles('dark', { bgcolor: 'primary.900' }),
                        '&:hover': { bgcolor: 'primary.lighter', ...theme.applyStyles('dark', { bgcolor: 'primary.darker' }) }
                      })
                  })}
                >
                  <item.icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} />
                </ListItemIcon>
              )}
              {drawerOpen && (
                <ListItemText
                  primary={
                    <Typography
                      variant="h6"
                      sx={{ color: isSelected ? iconSelectedColor : textColor, fontWeight: 600, fontSize: '0.875rem' }}
                    >
                      {item.title}
                    </Typography>
                  }
                  sx={{ flex: 1, my: 0 }}
                />
              )}
              {hasChildren && drawerOpen && (
                <IconButton
                  data-toggle="true"
                  onClick={(event) => {
                    event?.stopPropagation();
                    event.preventDefault();
                    handleToggle(item.id);
                  }}
                  color="secondary"
                  variant="outlined"
                  sx={{
                    width: 20,
                    height: 20,
                    mr: 0,
                    color: 'secondary.dark',
                    borderColor: isSelected || hasSelectedChild ? 'primary.light' : 'secondary.light',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    '&:hover': { 
                      borderColor: isSelected || hasSelectedChild ? 'primary.main' : 'secondary.main',
                      backgroundColor: 'transparent'
                    },
                    ...((isSelected || hasSelectedChild) && { 
                      color: 'primary.main',
                      borderColor: 'primary.light'
                    })
                  }}
                >
                  {isExpanded ? (
                    <UpOutlined style={{ fontSize: '0.625rem' }} />
                  ) : (
                    <DownOutlined style={{ fontSize: '0.625rem' }} />
                  )}
                </IconButton>
              )}
            </ListItemButton>
            
            {hasChildren && drawerOpen && (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit sx={{ mt: '-0.5rem' }}>
                <List
                  component="div"
                  disablePadding
                  sx={(theme) => ({
                    position: 'relative',
                    // Hierarchy rail in the indent gutter (neutral, a bit stronger than divider)
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: '2.5rem', // ~40px at 16px root — under parent icon column
                      top: 0,
                      bottom: '0.15rem',
                      width: '2px',
                      bgcolor: theme.palette.grey[400],
                      borderRadius: '1px',
                      ...theme.applyStyles('dark', { bgcolor: theme.palette.grey[600] })
                    },
                    // First child: flush under parent
                    '& > .MuiBox-root:first-of-type > .MuiListItemButton-root': {
                      paddingTop: 0,
                      minHeight: '2rem'
                    }
                  })}
                >
                  {item.children.map((child) => {
                    const isChildSelected = pathname === child.url;
                    
                    return (
                      <Box key={child.id} sx={{ position: 'relative' }}>
                        <ListItemButton
                          component={Link}
                          to={child.url}
                          selected={isChildSelected}
                          sx={(theme) => ({
                            zIndex: 1201,
                            // Child indent: was 56px (3.5rem); +12px at 16px root → 4.25rem
                            pl: '4.25rem',
                            // Tighter child rows (~36px at 16px root)
                            minHeight: '2.25rem',
                            py: 0,
                            '&:hover': { 
                              bgcolor: 'primary.lighter', 
                              ...theme.applyStyles('dark', { bgcolor: 'divider' }) 
                            },
                            // Selected child: paler than parent wash + left indicator
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              ...theme.applyStyles('dark', { bgcolor: alpha(theme.palette.primary.light, 0.12) }),
                              borderLeft: '3px solid',
                              borderLeftColor: 'primary.main',
                              color: textColor,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                ...theme.applyStyles('dark', { bgcolor: alpha(theme.palette.primary.light, 0.18) })
                              }
                            }
                          })}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                variant="h6"
                                sx={{
                                  color: isChildSelected ? iconSelectedColor : textColor,
                                  fontWeight: isChildSelected ? 500 : 400,
                                  fontSize: '0.8125rem'
                                }}
                              >
                                {child.title}
                              </Typography>
                            }
                            sx={{ my: 0 }}
                          />
                        </ListItemButton>
                      </Box>
                    );
                  })}
                </List>
              </Collapse>
            )}
          </Box>
        );
      }
      
      // Handle simple item type
      if (item.type === 'item') {
        const level = 1; // All top-level items are level 1
        const isSelected = pathname === item.url;
        
        return (
          <Box key={item.id} sx={{ position: 'relative' }}>
            <ListItemButton
              component={Link}
              to={item.url}
              selected={isSelected}
              sx={(theme) => ({
                zIndex: 1201,
                ...(drawerOpen ? { pl: 2, pr: 2, minHeight: '3.25rem', py: 0, ...parentRowHighlight(theme) } : { pl: 1.5, pr: 1.5, py: 1.25 }),
                ...(!drawerOpen && {
                  '&:hover': { bgcolor: 'transparent' },
                  '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' }
                })
              })}
            >
              {item.icon && (
                <ListItemIcon
                  sx={(theme) => ({
                    minWidth: 28,
                    color: isSelected ? iconSelectedColor : textColor,
                    ...(!drawerOpen && {
                      borderRadius: 1.5,
                      width: 36,
                      height: 36,
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': { bgcolor: 'secondary.lighter', ...theme.applyStyles('dark', { bgcolor: 'secondary.light' }) }
                    }),
                    ...(!drawerOpen &&
                      isSelected && {
                        bgcolor: 'primary.lighter',
                        ...theme.applyStyles('dark', { bgcolor: 'primary.900' }),
                        '&:hover': { bgcolor: 'primary.lighter', ...theme.applyStyles('dark', { bgcolor: 'primary.darker' }) }
                      })
                  })}
                >
                  <item.icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} />
                </ListItemIcon>
              )}
              {drawerOpen && (
                <ListItemText
                  primary={
                    <Typography
                      variant="h6"
                      sx={{ color: isSelected ? iconSelectedColor : textColor, fontWeight: 600, fontSize: '0.875rem' }}
                    >
                      {item.title}
                    </Typography>
                  }
                  sx={{ my: 0 }}
                />
              )}
            </ListItemButton>
          </Box>
        );
      }
      
      // Handle divider type (separator)
      if (item.type === 'divider') {
        return (
          <Divider 
            key={item.id} 
            sx={{ 
              my: 1.5,
              mx: drawerOpen ? 2 : 1
            }} 
          />
        );
      }
      
      return null;
    });
  }, [pathname, menuItems.items, selectedID, textColor, iconSelectedColor, drawerOpen]);

  return (
    <Box sx={{ pt: 2 }}>
      {navItems}
    </Box>
  );
}